import { Injector } from '@angular/core';
import {
  MODAL_TYPE,
  ModalComponentInterface,
  ModalConfig,
  ModalService,
} from '../../../../shared/components/modal/modal.service';
import { GridColumnActionType } from '../../../../shared/components/grid/grid-config';
import { ScheduledPaymentModel } from '../../models/scheduled-payment.model';
import { ScheduledPaymentsService } from '../../services/scheduled-payments.service';
import { NotificationService } from '../../../../shared/services/notification.service';
import { ScheduledPaymentFormComponent } from '../scheduled-payment-form/scheduled-payment-form.component';
import { ScheduledPaymentDetailsComponent } from '../scheduled-payment-details/scheduled-payment-details.component';
import { CustomersService } from '../../services/customers.service';
import { PermissionService } from '../../../../core/services/permission.service';

enum ActionNames {
  'viewDetails' = 'viewDetails',
  'update' = 'update',
  'delete' = 'delete',
  'pause' = 'pause',
  'resume' = 'resume',
  'skipNext' = 'skipNext',
}

const openModal = (injector: Injector, data: ModalConfig): void => {
  const modalService = injector!.get(ModalService);
  modalService.openModal(data);
};

const availableActions: { [key: string]: GridColumnActionType<ScheduledPaymentModel> } = {
  viewDetails: {
    label: 'View Details',
    handler: (payment: ScheduledPaymentModel, component?: Object, injector?: Injector) => {
      if (!payment.id) {
        console.error('Payment ID is missing');
        return;
      }

      const modalService = injector!.get(ModalService);
      const customersService = injector!.get(CustomersService);
      const scheduledPaymentsService = injector!.get(ScheduledPaymentsService);

      // Fetch full payment with history and payment methods
      scheduledPaymentsService.getScheduledPayment(payment.customerId, payment.id).subscribe({
        next: (fullPayment) => {
          customersService.getPaymentMethods(payment.customerId).subscribe((cards) => {
            modalService.openModal({
              title: 'Scheduled Payment Details',
              body: ScheduledPaymentDetailsComponent,
              type: MODAL_TYPE.MODAL,
              data: {
                payment: fullPayment, // Use full payment with history
                paymentMethods: cards,
              },
              confirm: null,
              dismiss: {
                label: 'Close',
                action: (modal: ModalComponentInterface) => {
                  modal.close();
                },
              },
            });
          });
        },
        error: (error) => {
          console.error('Error fetching scheduled payment details:', error);
          // Fallback to showing payment without history
          customersService.getPaymentMethods(payment.customerId).subscribe((cards) => {
            modalService.openModal({
              title: 'Scheduled Payment Details',
              body: ScheduledPaymentDetailsComponent,
              type: MODAL_TYPE.MODAL,
              data: {
                payment: payment,
                paymentMethods: cards,
              },
              confirm: null,
              dismiss: {
                label: 'Close',
                action: (modal: ModalComponentInterface) => {
                  modal.close();
                },
              },
            });
          });
        },
      });
    },
    canShow: (payment: ScheduledPaymentModel, component?: Object, injector?: Injector) => {
      if (!injector) return false;
      const permissionService = injector.get(PermissionService);
      return permissionService.hasPermission('SCHEDULED_PAYMENT_GET');
    },
  },
  update: {
    label: 'Update Payment',
    handler: (payment: ScheduledPaymentModel, component?: Object, injector?: Injector) => {
      // Validate payment has an ID
      if (!payment.id) {
        console.error('Payment ID is missing');
        return;
      }

      const modalService = injector!.get(ModalService);
      const scheduledPaymentsService = injector!.get(ScheduledPaymentsService);
      const customersService = injector!.get(CustomersService);
      const notificationService = injector!.get(NotificationService);

      const paymentId = payment.id; // Store in const for type narrowing

      // Get payment methods to populate the card selector
      customersService.getPaymentMethods(payment.customerId).subscribe((cards) => {
        openModal(injector!, {
          title: 'Update Scheduled Payment',
          body: ScheduledPaymentFormComponent,
          type: MODAL_TYPE.MODAL,
          data: {
            customerId: payment.customerId,
            paymentMethods: cards,
            scheduledPayment: payment, // Pass the existing payment for editing
          },
          confirm: {
            label: 'Update Schedule',
            action: (modal: ModalComponentInterface, formComponent: ScheduledPaymentFormComponent) => {
              const form = formComponent.form;
              if (!form.valid) {
                form.markAllAsTouched();
                return;
              }

              const formData = formComponent.getFormData();

              // Update the scheduled payment - send all form data
              // Note: undefined fields will be removed by the service before sending to backend
              const updateRequest = {
                tokenId: formData.tokenId,
                description: formData.description,
                referenceNumber: formData.referenceNumber,
                frequency: formData.frequency,
                weeklyAmount: formData.weeklyAmount,
                totalAmount: formData.totalAmount,
                numberOfWeeks: formData.numberOfWeeks,
                startDate: formData.startDate,
                endDate: formData.endDate,
                capitalCostEnabled: formData.capitalCostEnabled,
                capitalCostPercentage: formData.capitalCostPercentage,
                capitalCostAmount: formData.capitalCostAmount,
              };

              scheduledPaymentsService.updateScheduledPayment(payment.customerId, paymentId, updateRequest).subscribe({
                next: () => {
                  notificationService.success('Scheduled payment updated successfully', 'ok', 3000);
                  modal.close();
                },
                error: (error) => {
                  console.error('Error updating scheduled payment', error);
                  notificationService.error('Failed to update scheduled payment', 'ok', 3000);
                },
              });
            },
          },
          dismiss: {
            label: 'Cancel',
            action: (modal: ModalComponentInterface) => {
              modal.close();
            },
          },
        });
      });
    },
    canShow: (payment: ScheduledPaymentModel, component?: Object, injector?: Injector) => {
      if (!injector) return false;
      const permissionService = injector.get(PermissionService);
      if (!permissionService.hasPermission('SCHEDULED_PAYMENT_UPDATE')) return false;

      // Can only update active or paused payments (not failed, completed, or cancelled)
      return payment.status === 'active' || payment.status === 'paused';
    },
  },
  delete: {
    label: 'Delete Payment',
    handler: (payment: ScheduledPaymentModel, component?: Object, injector?: Injector) => {
      // Validate payment has an ID
      if (!payment.id) {
        console.error('Payment ID is missing');
        return;
      }

      const scheduledPaymentsService = injector!.get(ScheduledPaymentsService);
      const notificationService = injector!.get(NotificationService);

      const paymentId = payment.id; // Store in const for type narrowing

      openModal(injector!, {
        title: 'Delete Scheduled Payment',
        body: `Are you sure you want to delete this scheduled payment? This action cannot be undone.`,
        confirm: {
          label: 'Delete',
          color: 'warn',
          action: (modal: ModalComponentInterface) => {
            scheduledPaymentsService.deleteScheduledPayment(payment.customerId, paymentId).subscribe({
              next: () => {
                notificationService.success('Scheduled payment deleted successfully', 'ok', 3000);
                modal.close();
              },
              error: (error) => {
                console.error('Error deleting scheduled payment', error);
                notificationService.error('Failed to delete scheduled payment', 'ok', 3000);
              },
            });
          },
        },
        dismiss: {
          label: 'Cancel',
          action: (modal: ModalComponentInterface) => {
            modal.close();
          },
        },
      });
    },
    canShow: (payment: ScheduledPaymentModel, component?: Object, injector?: Injector) => {
      if (!injector) return false;
      const permissionService = injector.get(PermissionService);
      if (!permissionService.hasPermission('SCHEDULED_PAYMENT_DELETE')) return false;

      // Can delete any payment that's not already completed or cancelled
      return payment.status !== 'completed' && payment.status !== 'cancelled';
    },
  },
  pause: {
    label: 'Pause Payment',
    handler: (payment: ScheduledPaymentModel, component?: Object, injector?: Injector) => {
      // Validate payment has an ID
      if (!payment.id) {
        console.error('Payment ID is missing');
        return;
      }

      const scheduledPaymentsService = injector!.get(ScheduledPaymentsService);
      const notificationService = injector!.get(NotificationService);

      const paymentId = payment.id; // Store in const for type narrowing

      openModal(injector!, {
        title: 'Pause Scheduled Payment',
        body: `Are you sure you want to pause this scheduled payment?`,
        confirm: {
          label: 'Pause',
          action: (modal: ModalComponentInterface) => {
            scheduledPaymentsService.pauseScheduledPayment(payment.customerId, paymentId).subscribe({
              next: () => {
                notificationService.success('Scheduled payment paused successfully', 'ok', 3000);
                modal.close();
              },
              error: (error) => {
                console.error('Error pausing scheduled payment', error);
                notificationService.error('Failed to pause scheduled payment', 'ok', 3000);
              },
            });
          },
        },
        dismiss: {
          label: 'Cancel',
          action: (modal: ModalComponentInterface) => {
            modal.close();
          },
        },
      });
    },
    canShow: (payment: ScheduledPaymentModel, component?: Object, injector?: Injector) => {
      if (!injector) return false;
      const permissionService = injector.get(PermissionService);
      if (!permissionService.hasPermission('SCHEDULED_PAYMENT_PAUSE')) return false;

      return payment.status === 'active';
    },
  },
  resume: {
    label: 'Resume Payment',
    handler: (payment: ScheduledPaymentModel, component?: Object, injector?: Injector) => {
      // Validate payment has an ID
      if (!payment.id) {
        console.error('Payment ID is missing');
        return;
      }

      const scheduledPaymentsService = injector!.get(ScheduledPaymentsService);
      const notificationService = injector!.get(NotificationService);

      const paymentId = payment.id; // Store in const for type narrowing

      openModal(injector!, {
        title: 'Resume Scheduled Payment',
        body: `Are you sure you want to resume this scheduled payment?`,
        confirm: {
          label: 'Resume',
          action: (modal: ModalComponentInterface) => {
            scheduledPaymentsService.resumeScheduledPayment(payment.customerId, paymentId).subscribe({
              next: () => {
                notificationService.success('Scheduled payment resumed successfully', 'ok', 3000);
                modal.close();
              },
              error: (error) => {
                console.error('Error resuming scheduled payment', error);
                notificationService.error('Failed to resume scheduled payment', 'ok', 3000);
              },
            });
          },
        },
        dismiss: {
          label: 'Cancel',
          action: (modal: ModalComponentInterface) => {
            modal.close();
          },
        },
      });
    },
    canShow: (payment: ScheduledPaymentModel, component?: Object, injector?: Injector) => {
      if (!injector) return false;
      const permissionService = injector.get(PermissionService);
      if (!permissionService.hasPermission('SCHEDULED_PAYMENT_RESUME')) return false;

      return payment.status === 'paused';
    },
  },
  skipNext: {
    label: 'Skip Next Payment',
    handler: (payment: ScheduledPaymentModel, component?: Object, injector?: Injector) => {
      // Validate payment has an ID
      if (!payment.id) {
        console.error('Payment ID is missing');
        return;
      }

      const scheduledPaymentsService = injector!.get(ScheduledPaymentsService);
      const notificationService = injector!.get(NotificationService);

      const paymentId = payment.id; // Store in const for type narrowing

      openModal(injector!, {
        title: 'Skip Next Payment',
        body: `Are you sure you want to skip the next scheduled payment? The payment will be rescheduled to the following period.`,
        confirm: {
          label: 'Skip Payment',
          color: 'primary',
          action: (modal: ModalComponentInterface) => {
            scheduledPaymentsService.skipNextPayment(payment.customerId, paymentId).subscribe({
              next: (updatedPayment) => {
                if (updatedPayment) {
                  notificationService.success('Next payment skipped successfully', 'ok', 3000);
                } else {
                  notificationService.error('Unable to skip payment', 'ok', 3000);
                }
                modal.close();
              },
              error: (error) => {
                console.error('Error skipping payment', error);
                notificationService.error('Failed to skip payment', 'ok', 3000);
              },
            });
          },
        },
        dismiss: {
          label: 'Cancel',
          action: (modal: ModalComponentInterface) => {
            modal.close();
          },
        },
      });
    },
    canShow: (payment: ScheduledPaymentModel, component?: Object, injector?: Injector) => {
      if (!injector) return false;
      const permissionService = injector.get(PermissionService);
      if (!permissionService.hasPermission('SCHEDULED_PAYMENT_SKIP')) return false;

      return payment.status === 'active' && !!payment.nextExecutionDate;
    },
  },
};

export const scheduledPaymentActions = [
  availableActions[ActionNames.viewDetails],
  availableActions[ActionNames.update],
  availableActions[ActionNames.pause],
  availableActions[ActionNames.resume],
  availableActions[ActionNames.skipNext],
  availableActions[ActionNames.delete],
];
